import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import FourOhFourPage from '../common/404Page';

import policyActions from '../policy/policyActions';
import quotingActions from './quotingActions';
import userActions from '../user/userActions';

import SimpleWorkflow from './simple/SimpleWorkflow';
import AdvisoryWorkflow from './advisory/AdvisoryWorkflow';
import BespokeWorkflow from './bespoke/BespokeWorkflow';
import CustomWorkflow from './custom/CustomWorkflow';
import { Page } from '../components/common';
import Utils from '../common/utils';
import { debounce } from 'lodash';
import authActions from '../auth/authActions';
import settingsActions from '../common/settingsActions';

interface Props {
	currentUser: UserDetails;
	user: UserDetails;
	customerDetails: CustomerDemographicDetails;
	customerFamilyList: CustomerFamily[];
	customerPolicy: CustomerPolicy;
	hwTable: HeightWeight[];
	planOptions: CustomerPolicy[];
	paymentDetails: any;
	questionnaire: any;
	stripe: any;
	assessment: any;
	validReferral: boolean;
	referrals: CustomerReferralResponse;
	actions: any;
	history: any;
	match: any;
}
interface State {
	workflow: React.StatelessComponent<any> | null;
}
class QuotingPage extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			workflow: null
		};
	}

	componentDidMount() {
		this.props.actions.fetchHeightWeightTable();
		// Set datepicker picked if birthdate already exists
		if (this.props.user.birthDate) {
			const age = Utils.getAgeRoundedFromBirthdate(this.props.user.birthDate);
			const customerDetails = {
				...this.props.customerDetails,
				age: age
			};
			this.props.actions.setCustomerDetails(customerDetails);
		}
		this.setWorkflow(this.props.match.params.workflow);
	}

	componentWillReceiveProps(nextProps: Props) {
		if (
			nextProps.match.params.workflow !== this.props.match.params.workflow ||
			nextProps.match.params.page !== this.props.match.params.page
		) {
			this.setWorkflow(nextProps.match.params.workflow);
		}
	}

	setWorkflow = (workflowParam: string) => {
		let workflow = null;
		if (workflowParam === 'A') {
			workflow = SimpleWorkflow;
		}
		else if (workflowParam === 'B') {
			workflow = AdvisoryWorkflow;
		}
		else if (workflowParam === 'C') {
			workflow = BespokeWorkflow;
		}
		else if (workflowParam === 'Custom') {
			workflow = CustomWorkflow;
		}

		this.setState({ workflow });
	}

	onUserChange = (e: any) => {
		let value = Utils.parseValueFromEvent(e);

		const user = {
			...this.props.user,
			[e.target.name]: value
		};

		this.props.actions.setUser(user);

		// Set user's age based on birthdate
		if (e.target.name === 'birthDate') {
			const age = Utils.getAgeRoundedFromBirthdate(e.target.value);
			const customerDetails = {
				...this.props.customerDetails,
				age: age
			};
			this.props.actions.setCustomerDetails(customerDetails);
		}
	}

	onCustomerDetailsChange = (e: any) => {
		if (e.target.type === 'text' && !e.target.validity.valid) {
			return;
		}

		let value = Utils.parseValueFromEvent(e);

		const customerDetails = {
			...this.props.customerDetails,
			[e.target.name]: value
		};

		this.props.actions.setCustomerDetails(customerDetails);
	}
	onCustomerDetailsNumberKeyUp = (e: any) => {
		const value = Utils.formatNumberWithCommas(e.target.value);

		const customerDetails = {
			...this.props.customerDetails,
			[e.target.name]: value
		};

		this.props.actions.setCustomerDetails(customerDetails);
	}

	onQuestionnairePaste = (e: any) => {
		e.preventDefault();
		const value = Utils.formatNumberWithCommas(e.clipboardData.getData('Text'));
		const questionnaire = {
			...this.props.questionnaire,
			[e.target.name]: value
		};

		this.props.actions.setQuestionnaire(questionnaire);
	}

	onQuestionnaireNumberKeyUp = (e: any) => {
		const value = Utils.formatNumberWithCommas(e.target.value);

		const questionnaire = {
			...this.props.questionnaire,
			[e.target.name]: value
		};

		this.props.actions.setQuestionnaire(questionnaire);
	}
	onQuestionnaireChange = (e: any) => {
		if (e.target.type === 'text' && !e.target.validity.valid) {
			return;
		}

		const value = Utils.parseValueFromEvent(e);

		const questionnaire = {
			...this.props.questionnaire,
			[e.target.name]: value
		};
		this.props.actions.setQuestionnaire(questionnaire);

		// onblur doesn't work on safari or firefox on apple shit
		// so if this is a checkbox or radio save immediately rather than onblur
		if (this.shouldSaveOnChangeRatherThanOnBlur(e.target.type)) {
			const formattedValue = this.stringifyAnswerIfRequired(value);
			if (this.shouldDebouncedUpdate(e.target.name)) {
				this.debounceUpdate(this.props.customerPolicy.id, e.target.name, formattedValue);
			}
			else {
				return this.props.actions
				.saveCustomerPolicyAnswer(this.props.customerPolicy.id, e.target.name, formattedValue);
			}
		}
		else {
			return;
		}
	}
	onQuestionnaireBlur = (e: any) => {

		// onblur doesn't work on safari or firefox on apple shit
		// so if this is a checkbox or radio the save has been handled on change rather than here
		if (!this.shouldSaveOnChangeRatherThanOnBlur(e.target.type)) {
			const value = Utils.parseValueFromEvent(e);
			const formattedValue = this.stringifyAnswerIfRequired(value);
			return this.props.actions.saveCustomerPolicyAnswer(this.props.customerPolicy.id, e.target.name, formattedValue);
		}
		else {
			return;
		}
	}
	shouldSaveOnChangeRatherThanOnBlur = (targetType: string) => {
		return (targetType === 'checkbox'
			|| targetType === 'checkboxSerial'
			|| targetType === 'radio'
			|| targetType === 'custom');
	}

	// Note: the value is from TextInput but the event is being wrapped by other type events,
	// so comparing event types here doesn't really work.
	// Also even user is typing 'other values', the system is just updating the answer object,
	// since 'other values' is just property of the object 
	shouldDebouncedUpdate = (targetName: string) => {
		return (targetName === 'cancer'
			|| targetName === 'heartCirculatory'
			|| targetName === 'neurological'
			|| targetName === 'respiratory'
			|| targetName === 'liver'
			|| targetName === 'blood'
			|| targetName === 'immuno'
			|| targetName === 'head'
			|| targetName === 'reproductive'
			|| targetName === 'psychological');
			
	}
	stringifyAnswerIfRequired = (value: any): string => {
		if (typeof value === 'object') {
			return JSON.stringify(value);
		}
		else {
			return value;
		}
	}

	// tslint:disable-next-line
	debounceUpdate = debounce((policyId: string | undefined, eventTargetName: string, jsonValue: string) => {
		// tslint:disable-next-line
		return this.props.actions.saveCustomerPolicyAnswer(policyId, eventTargetName, jsonValue); }, 500);

	onCustomerPolicySelect = (customerPolicy: CustomerPolicy) => {
		this.props.actions.setSelectPlanOptionsEnabled(false);
		if (this.props.customerPolicy.campaignCode) {
			customerPolicy.campaignCode = this.props.customerPolicy.campaignCode;
		}
		this.props.actions.setCustomerPolicy(customerPolicy);
		this.saveAndRedirect(`/quote/${this.props.match.params.workflow}/enrollment/my-pledge`);
	}

	save = () => {
		return this.props.actions.saveUser(this.props.user)
			.then(() => {
				const customerDetails: CustomerDemographicDetails = {
					...this.props.customerDetails,
					customerId: this.props.user.id
				};
				return this.props.actions.saveCustomerDetails(customerDetails);
			})
			.then(() => this.props.actions.saveCustomerFamilyList(this.props.user.id, this.props.customerFamilyList))
			.then(() => {
				const referralCode = this.props.referrals.referralCode;
				const customerPolicy: CustomerPolicy = {
					...this.props.customerPolicy,
					customerId: this.props.user.id,
				};
				if (referralCode && !this.props.customerPolicy.campaignCode) {
					customerPolicy.campaignCode = referralCode;
				}
				return this.props.actions.saveCustomerPolicy(customerPolicy);
			})
			.then(() => {
				// Save about you form fields to questionnaire
				const p1 = this.props.actions
					.saveCustomerPolicyAnswer(this.props.customerPolicy.id, 'birthDate', this.props.user.birthDate);
				const p2 = this.props.actions
					.saveCustomerPolicyAnswer(this.props.customerPolicy.id, 'gender', this.props.user.gender);
				// TODO: no isSmoker column in DB
				const p3 = this.props.actions
					.saveCustomerPolicyAnswer(this.props.customerPolicy.id, 'isSmoker', this.props.customerDetails.isSmoker);
				return Promise.all([p1, p2, p3]);
			});
	}

	saveAndRedirect = (url: string) => {
		this.save()
			.then(() => {
				this.props.actions.setSelectPlanOptionsEnabled(true);
				this.props.history.push(url);
			});
	}

	render() {
		if (['a', 'b', 'c', 'custom'].indexOf(this.props.match.params.workflow.toLowerCase()) === -1) {
			return <FourOhFourPage />;
		}

		let Workflow = this.state.workflow;

		return (
			<Page>
				{Workflow && (
					<Workflow
						{...this.props}
						onUserChange={this.onUserChange}
						onCustomerDetailsChange={this.onCustomerDetailsChange}
						onCustomerDetailsNumberKeyUp={this.onCustomerDetailsNumberKeyUp}
						onQuestionnairePaste={this.onQuestionnairePaste}
						onCustomerPolicySelect={this.onCustomerPolicySelect}
						onQuestionnaireBlur={this.onQuestionnaireBlur}
						onQuestionnaireChange={this.onQuestionnaireChange}
						onQuestionnaireNumberKeyUp={this.onQuestionnaireNumberKeyUp}
						saveAndRedirect={this.saveAndRedirect}
					/>
				)}
			</Page>
		);
	}
}
function mapStateToProps(state: any) {
	return {
		user: state.user.user,
		referrals: state.user.referrals,
		customerDetails: state.quoting.customerDetails,
		customerFamilyList: state.quoting.customerFamilyList,
		customerPolicy: state.policy.customerPolicy,
		hwTable: state.quoting.hwTable,
		planOptions: state.policy.planOptions,
		paymentDetails: state.payment.details,
		questionnaire: state.policy.questionnaire,
		stripe: state.payment.stripe,

		assessment: state.quoting.assessment,
		currentUser: state.auth.currentUser,
		validReferral: state.quoting.validReferral,
	};
}
function mapDispatchToProps(dispatch: any) {
	const actions = { ...policyActions, ...quotingActions, ...userActions, ...authActions, ...settingsActions };
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(QuotingPage));



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/quoting/QuotingPage.tsx